import React, { useState, useEffect, Suspense, lazy } from 'react';
import { API_PREFIX } from 'src/config/api';
import { Routes, Route, useNavigate } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import { useAppSelector, useAppDispatch } from 'src/hooks/redux';
import { setCurrentProject } from 'src/store/slices/projectSlice';
import ErrorBoundary from 'src/components/UI/ErrorBoundary';
import PerformanceMonitor from 'src/components/UI/PerformanceMonitor';
import LoadingScreen from './LoadingScreen';
import MenuBar from './MenuBar';
import ToolBar from './ToolBar';
import NewProjectDialog from 'src/components/Project/NewProjectDialog';
import ProjectListModal from 'src/components/Project/ProjectListModal';
import InteractiveTour from 'src/components/Tutorial/InteractiveTour';
import GettingStartedWizard from 'src/components/Tutorial/GettingStartedWizard';
import WelcomeScreen from 'src/components/Tutorial/WelcomeScreen';
import { addNotification } from 'src/store/slices/uiSlice';
import {
  startTour,
  stopTour,
  setWelcomeScreenShown,
  updateTutorialProgress,
} from 'src/store/slices/tutorialSlice';
import { mainTourSteps } from 'src/config/tourSteps';

// Lazy load all page components for code splitting
const DashboardPage = lazy(() => import('src/pages/Dashboard'));
const FlowEditorPage = lazy(() => import('src/pages/FlowEditor'));
const Simulation3DPage = lazy(() => import('src/pages/Simulation3D'));
const SettingsPage = lazy(() => import('src/pages/Settings'));
const MarketplacePage = lazy(() => import('src/pages/Marketplace/MarketplacePage'));
const ModuleManagerPage = lazy(() => import('src/pages/ModuleManager/ModuleManagerPage'));
const TutorialPage = lazy(() => import('src/pages/Help/TutorialPage'));
const DocumentationPage = lazy(() => import('src/pages/Help/DocumentationPage'));
const RobotControlPage = lazy(() => import('src/pages/RobotControl'));
const ProfilePage = lazy(() => import('src/pages/ProfilePage'));
const PersonalizationPage = lazy(() => import('src/pages/Personalization'));
const LearningPathsPage = lazy(() => import('src/pages/Learning/LearningPathsPage'));
const LearningPathDetailPage = lazy(() => import('src/pages/Learning/LearningPathDetailPage'));
const ModuleDetailPage = lazy(() => import('src/pages/Learning/ModuleDetailPage'));
const CodeExamplesPage = lazy(() => import('src/pages/Learning/CodeExamplesPage'));
const ProjectGalleryPage = lazy(() => import('src/pages/ProjectTemplates/ProjectGalleryPage'));
const RobotBuilderPage = lazy(() => import('src/pages/RobotBuilder/RobotBuilderPage'));
const RobotConfigPage = lazy(() => import('src/pages/RobotConfig/RobotConfigPage'));
const SensorDashboardPage = lazy(() => import('src/pages/Sensors/SensorDashboardPage'));
const ROS2CodeGeneratorPage = lazy(() => import('src/pages/ROS2/ROS2CodeGeneratorPage'));
const ROS2TopicMonitorPage = lazy(() => import('src/pages/ROS2/ROS2TopicMonitorPage'));
const ROS2ServiceTesterPage = lazy(() => import('src/pages/ROS2/ROS2ServiceTesterPage'));
const ROS2ActionMonitorPage = lazy(() => import('src/pages/ROS2/ROS2ActionMonitorPage'));
const ROS2LaunchEditorPage = lazy(() => import('src/pages/ROS2/ROS2LaunchEditorPage'));
const ROS2TopicGraphPage = lazy(() => import('src/pages/ROS2/ROS2TopicGraphPage'));
const ROS2PackageManagerPage = lazy(() => import('src/pages/ROS2/ROS2PackageManagerPage'));
const WorldBuilderPage = lazy(() => import('src/pages/Simulation/WorldBuilderPage'));
const MultiRobotSimPage = lazy(() => import('src/pages/Simulation/MultiRobotSimPage'));
const ScenarioCreatorPage = lazy(() => import('src/pages/Simulation/ScenarioCreatorPage'));
const ImageProcessingPage = lazy(() => import('src/pages/AI/ImageProcessingPage'));
const MLTrainingPage = lazy(() => import('src/pages/AI/MLTrainingPage'));
const DatasetManagerPage = lazy(() => import('src/pages/AI/DatasetManagerPage'));
const ModelLibraryPage = lazy(() => import('src/pages/AI/ModelLibraryPage'));
const ReinforcementLearningPage = lazy(() => import('src/pages/AI/ReinforcementLearningPage'));
const RemoteSimulatorPage = lazy(() => import('src/pages/Remote/RemoteSimulatorPage'));
const SLAMPage = lazy(() => import('src/pages/Advanced/SLAMPage'));
const PathPlanningPage = lazy(() => import('src/pages/Advanced/PathPlanningPage'));
const RobotArmControlPage = lazy(() => import('src/pages/Advanced/RobotArmControlPage'));
const ProjectExportPage = lazy(() => import('src/pages/Advanced/ProjectExportPage'));
const RealRobotIntegrationPage = lazy(() => import('src/pages/Advanced/RealRobotIntegrationPage'));
const FAQPage = lazy(() => import('src/pages/Help/FAQPage'));
const APIDocumentationPage = lazy(() => import('src/pages/Help/APIDocumentationPage'));
const TestBuilderPage = lazy(() => import('src/pages/Testing/TestBuilderPage'));
const TestExecutorPage = lazy(() => import('src/pages/Testing/TestExecutorPage'));
const TestCoveragePage = lazy(() => import('src/pages/Testing/TestCoveragePage'));
const CodeQualityPage = lazy(() => import('src/pages/Testing/CodeQualityPage'));
const PerformanceMonitorPage = lazy(() => import('src/pages/Testing/PerformanceMonitorPage'));



const MainLayout: React.FC = () => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const { theme } = useAppSelector((state) => state.ui as any);
    const { currentProject } = useAppSelector((state) => (state.project as any));
    const {
        welcomeScreenShown,
        gettingStartedCompleted,
    } = useAppSelector((state) => state.tutorial);
    const [newProjectDialogOpen, setNewProjectDialogOpen] = useState(false);
    const [projectListOpen, setProjectListOpen] = useState(false);
    const [welcomeScreenOpen, setWelcomeScreenOpen] = useState(false);
    const [wizardOpen, setWizardOpen] = useState(false);
    const [tourOpen, setTourOpen] = useState(false);

    // Check if first-time user and show welcome screen
    useEffect(() => {
        if (!welcomeScreenShown) {
            setWelcomeScreenOpen(true);
        }
    }, [welcomeScreenShown]);

    // Show wizard if welcome screen was shown but wizard not completed
    useEffect(() => {
        if (welcomeScreenShown && !gettingStartedCompleted && !welcomeScreenOpen) {
            // Small delay to let welcome screen close first
            const timer = setTimeout(() => {
                setWizardOpen(true);
            }, 500);
            return () => clearTimeout(timer);
        }
    }, [welcomeScreenShown, gettingStartedCompleted, welcomeScreenOpen]);

    const handleNavigate = (path: string) => {
        navigate(path);
    };

    const handleWelcomeClose = () => {
        setWelcomeScreenOpen(false);
        dispatch(setWelcomeScreenShown(true));
    };

    const handleWelcomeDontShowAgain = () => {
        dispatch(setWelcomeScreenShown(true));
        setWelcomeScreenOpen(false);
    };

    const handleWelcomeNewProject = () => {
        setWelcomeScreenOpen(false);
        dispatch(setWelcomeScreenShown(true));
        handleNewProject();
    };

    const handleWelcomeBrowseTemplates = () => {
        setWelcomeScreenOpen(false);
        dispatch(setWelcomeScreenShown(true));
        navigate('/marketplace');
    };

    const handleWelcomeTakeTour = () => {
        setWelcomeScreenOpen(false);
        dispatch(setWelcomeScreenShown(true));
        handleStartTour();
    };

    const handleStartTour = () => {
        dispatch(startTour('main-tour'));
        setTourOpen(true);
    };

    const handleTourComplete = () => {
        dispatch(stopTour());
        setTourOpen(false);
        dispatch(
            updateTutorialProgress({
                tutorialId: 'main-tour',
                userId: 'current-user', // TODO: Get from auth
                currentStep: mainTourSteps.length,
                totalSteps: mainTourSteps.length,
                completedSteps: Array.from({ length: mainTourSteps.length }, (_, i) => i),
                startedAt: new Date(),
                lastAccessedAt: new Date(),
                completedAt: new Date(),
                skipped: false,
            })
        );
        dispatch(
            addNotification({
                type: 'success',
                title: 'Tour Completed',
                message: 'Great job! You\'ve completed the tour. Explore Telix Lab to learn more.',
            })
        );
    };

    const handleTourSkip = () => {
        dispatch(stopTour());
        setTourOpen(false);
        dispatch(
            updateTutorialProgress({
                tutorialId: 'main-tour',
                userId: 'current-user', // TODO: Get from auth
                currentStep: 0,
                totalSteps: mainTourSteps.length,
                completedSteps: [],
                startedAt: new Date(),
                lastAccessedAt: new Date(),
                skipped: true,
            })
        );
    };

    const handleWizardComplete = () => {
        setWizardOpen(false);
        dispatch(
            addNotification({
                type: 'success',
                title: 'Welcome!',
                message: 'Your preferences have been saved. Let\'s start building!',
            })
        );
    };

    const handleNewProject = () => {
        setNewProjectDialogOpen(true);
    };

    const handleOpenProject = () => {
        setProjectListOpen(true);
    };

    const handleSaveProject = () => {
        (async () => {
            if (!currentProject) {
                dispatch(addNotification({ type: 'warning', title: 'Save Project', message: 'No open project to save.' }));
                return;
            }
            try {
                const payload = { ...currentProject };
                // serialize Date fields
                if (payload.created instanceof Date) payload.created = payload.created.toISOString();
                if (payload.modified instanceof Date) payload.modified = (new Date()).toISOString();
                const res = await fetch(`${API_PREFIX}/api/projects`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(payload)
                });
                if (!res.ok) throw new Error('Failed to save project');
                dispatch(addNotification({ type: 'success', title: 'Save Project', message: 'Project saved successfully.' }));
            } catch (err: any) {
                dispatch(addNotification({ type: 'error', title: 'Save Failed', message: err?.message || String(err) }));
            }
        })();
    };

    const handleRunProject = () => {
        // TODO: Implement run project functionality
        dispatch(addNotification({
            type: 'success',
            title: 'Project Running',
            message: 'Project execution started.'
        }));
    };

    const handleStopProject = () => {
        // TODO: Implement stop project functionality
        dispatch(addNotification({
            type: 'warning',
            title: 'Project Stopped',
            message: 'Project execution stopped.'
        }));
    };

    const handleSaveAs = () => {
        (async () => {
            if (!currentProject) {
                dispatch(addNotification({ type: 'warning', title: 'Save As', message: 'No open project to save.' }));
                return;
            }
            const name = window.prompt('Enter name for copy of project', `${currentProject.name || 'Untitled'} (Copy)`);
            if (!name) return;
            try {
                const copy = {
                    ...currentProject,
                    id: crypto.randomUUID(),
                    name,
                    created: (new Date()).toISOString(),
                    modified: (new Date()).toISOString()
                } as any;
                const res = await fetch(`${API_PREFIX}/api/projects`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(copy)
                });
                if (!res.ok) throw new Error('Failed to save project copy');
                // set as current
                copy.created = new Date(copy.created);
                copy.modified = new Date(copy.modified);
                dispatch(setCurrentProject(copy));
                dispatch(addNotification({ type: 'success', title: 'Save As', message: 'Project saved as copy.' }));
            } catch (err: any) {
                dispatch(addNotification({ type: 'error', title: 'Save As Failed', message: err?.message || String(err) }));
            }
        })();
    };

    const handleDownloadTlx = () => {
        if (!currentProject) {
            dispatch(addNotification({ type: 'warning', title: 'Download .tlx', message: 'No open project to download.' }));
            return;
        }
        try {
            const dataStr = JSON.stringify(currentProject, null, 2);
            const blob = new Blob([dataStr], { type: 'application/json' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `${(currentProject.name || 'project').replace(/[^a-z0-9-_]/gi, '_')}.tlx`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
            dispatch(addNotification({ type: 'success', title: 'Download .tlx', message: 'Project downloaded as .tlx file.' }));
        } catch (err: any) {
            dispatch(addNotification({ type: 'error', title: 'Download Failed', message: err?.message || String(err) }));
        }
    };

    const handleExport = () => {
        // TODO: Export functionality
        dispatch(addNotification({
            type: 'info',
            title: 'Export',
            message: 'Export options menu opened.'
        }));
    };

    const handleUndo = () => {
        dispatch(addNotification({
            type: 'info',
            title: 'Undo',
            message: 'Undo action performed.'
        }));
    };

    const handleRedo = () => {
        dispatch(addNotification({
            type: 'info',
            title: 'Redo',
            message: 'Redo action performed.'
        }));
    };

    const handleProjectSettings = () => {
        dispatch(addNotification({
            type: 'info',
            title: 'Project Settings',
            message: 'Project settings dialog opened.'
        }));
    };

    const handleTogglePanel = (panel: string) => {
        dispatch(addNotification({
            type: 'info',
            title: 'Toggle Panel',
            message: `${panel} panel toggled.`
        }));
    };

    const handle3DViewOptions = () => {
        dispatch(addNotification({
            type: 'info',
            title: '3D View Options',
            message: '3D view options dialog opened.'
        }));
    };

    const handleRunSimulation = () => {
        dispatch(addNotification({
            type: 'success',
            title: 'Run Simulation',
            message: 'Simulation started successfully.'
        }));
    };

    const handleStopSimulation = () => {
        dispatch(addNotification({
            type: 'warning',
            title: 'Stop Simulation',
            message: 'Simulation stopped.'
        }));
    };

    const handleConnectRobot = () => {
        dispatch(addNotification({
            type: 'info',
            title: 'Connect Robot',
            message: 'Robot connection dialog opened.'
        }));
    };

    const handleManualControl = () => {
        dispatch(addNotification({
            type: 'info',
            title: 'Manual Control',
            message: 'Manual robot control opened.'
        }));
    };

    const handleDocumentation = () => {
        dispatch(addNotification({
            type: 'info',
            title: 'Documentation',
            message: 'Opening Telix documentation...'
        }));
    };

    return (
        <ErrorBoundary>
            <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
            {/* Welcome Screen */}
            {welcomeScreenOpen && (
                <WelcomeScreen
                    onNewProject={handleWelcomeNewProject}
                    onBrowseTemplates={handleWelcomeBrowseTemplates}
                    onTakeTour={handleWelcomeTakeTour}
                    onClose={handleWelcomeClose}
                    onDontShowAgain={handleWelcomeDontShowAgain}
                />
            )}

            {/* Getting Started Wizard */}
            <GettingStartedWizard
                open={wizardOpen}
                onClose={() => setWizardOpen(false)}
                onComplete={handleWizardComplete}
            />

            {/* Interactive Tour */}
            <InteractiveTour
                open={tourOpen}
                steps={mainTourSteps}
                tourId="main-tour"
                onClose={handleTourSkip}
                onComplete={handleTourComplete}
                onSkip={handleTourSkip}
            />

            {/* Menu Bar */}
            <MenuBar
                onNewProject={handleNewProject}
                onOpenProject={handleOpenProject}
                onSaveProject={handleSaveProject}
                onSaveAs={handleSaveAs}
                onDownloadTlx={handleDownloadTlx}
                onExport={handleExport}
                onUndo={handleUndo}
                onRedo={handleRedo}
                onProjectSettings={handleProjectSettings}
                onTogglePanel={handleTogglePanel}
                on3DViewOptions={handle3DViewOptions}
                onRunSimulation={handleRunSimulation}
                onStopSimulation={handleStopSimulation}
                onConnectRobot={handleConnectRobot}
                onManualControl={handleManualControl}
                onDocumentation={handleDocumentation}
                onNavigate={handleNavigate}
                onTakeTour={handleStartTour}
                currentTheme={theme}
            />

            {/* Tool Bar */}
            <ToolBar
                onNewProject={handleNewProject}
                onOpenProject={handleOpenProject}
                onSaveProject={handleSaveProject}
                onRunProject={handleRunProject}
                onStopProject={handleStopProject}
                currentTheme={theme}
            />

            {/* Main Content Area */}
            <Box
                component="main"
                data-tour="dashboard"
                sx={{
                    flexGrow: 1,
                    p: { xs: 1, sm: 2, md: 3 },
                    bgcolor: theme === 'dark' ? 'grey.900' : 'grey.50',
                    overflow: 'auto'
                }}
            >
                <Suspense fallback={
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', minHeight: 400 }}>
                        <CircularProgress />
                    </Box>
                }>
                    <Routes>
                    <Route path="/" element={<DashboardPage />} />
                    {/* Protected routes - authentication will be added later */}
                    <Route path="/flow" element={<FlowEditorPage />} />
                    <Route path="/simulation" element={<Simulation3DPage />} />
                    <Route path="/robot" element={<RobotControlPage />} />
                    <Route path="/monitoring" element={<DashboardPage />} />
                    <Route path="/settings" element={<SettingsPage />} />
                    <Route path="/profile" element={<ProfilePage />} />
                    <Route path="/personalization" element={<PersonalizationPage />} />
                    <Route path="/marketplace" element={<MarketplacePage />} />
                    <Route path="/modules" element={<ModuleManagerPage />} />
                    <Route path="/help/tutorial" element={<TutorialPage />} />
                    <Route path="/help/documentation" element={<DocumentationPage />} />
                    <Route path="/help/faq" element={<FAQPage />} />
                    <Route path="/help/api" element={<APIDocumentationPage />} />
                    <Route path="/learning" element={<LearningPathsPage />} />
                    <Route path="/learning/path/:pathId" element={<LearningPathDetailPage />} />
                    <Route path="/learning/module/:moduleId" element={<ModuleDetailPage />} />
                    <Route path="/learning/code-examples" element={<CodeExamplesPage />} />
                    <Route path="/templates" element={<ProjectGalleryPage />} />
                    <Route path="/robot-builder" element={<RobotBuilderPage />} />
                    <Route path="/robot-config" element={<RobotConfigPage />} />
                    <Route path="/sensors" element={<SensorDashboardPage />} />
                    <Route path="/ros2/code-generator" element={<ROS2CodeGeneratorPage />} />
                    <Route path="/ros2/topics" element={<ROS2TopicMonitorPage />} />
                    <Route path="/ros2/services" element={<ROS2ServiceTesterPage />} />
                    <Route path="/ros2/actions" element={<ROS2ActionMonitorPage />} />
                    <Route path="/ros2/launch-editor" element={<ROS2LaunchEditorPage />} />
                    <Route path="/ros2/topic-graph" element={<ROS2TopicGraphPage />} />
                    <Route path="/ros2/packages" element={<ROS2PackageManagerPage />} />
                    <Route path="/simulation/world-builder" element={<WorldBuilderPage />} />
                    <Route path="/simulation/multi-robot" element={<MultiRobotSimPage />} />
                    <Route path="/simulation/scenarios" element={<ScenarioCreatorPage />} />
                    <Route path="/ai/image-processing" element={<ImageProcessingPage />} />
                    <Route path="/ai/ml-training" element={<MLTrainingPage />} />
                    <Route path="/ai/datasets" element={<DatasetManagerPage />} />
                    <Route path="/ai/models" element={<ModelLibraryPage />} />
                    <Route path="/ai/reinforcement-learning" element={<ReinforcementLearningPage />} />
                    <Route path="/remote/simulators" element={<RemoteSimulatorPage />} />
                    <Route path="/advanced/slam" element={<SLAMPage />} />
                    <Route path="/advanced/path-planning" element={<PathPlanningPage />} />
                    <Route path="/advanced/robot-arm" element={<RobotArmControlPage />} />
                    <Route path="/advanced/export" element={<ProjectExportPage />} />
                    <Route path="/advanced/real-robot" element={<RealRobotIntegrationPage />} />
                    <Route path="/testing/builder" element={<TestBuilderPage />} />
                    <Route path="/testing/executor" element={<TestExecutorPage />} />
                    <Route path="/testing/coverage" element={<TestCoveragePage />} />
                    <Route path="/testing/quality" element={<CodeQualityPage />} />
                    <Route path="/testing/performance" element={<PerformanceMonitorPage />} />
                    </Routes>
                </Suspense>
            </Box>

            {/* New Project Dialog */}
            <NewProjectDialog
                open={newProjectDialogOpen}
                onClose={() => setNewProjectDialogOpen(false)}
            />
            {/* Project List Modal (Open Project) */}
            <ProjectListModal
                open={projectListOpen}
                onClose={() => setProjectListOpen(false)}
            />
            <PerformanceMonitor />
        </Box>
        </ErrorBoundary>
    );
};

export default MainLayout;